/**
 * EditDossier - Édition d'un dossier SEO
 * Refactorisé : logique dans useDossierActions, UI dans composants extraits
 */
import React, { useState, useMemo } from 'react';
import { toast } from '../../App';
import { Sparkles, CalendarClock, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { suggestedDateTime } from './constants';

// Hook et composants extraits
import { useDossierActions } from './hooks/useDossierActions';
import {
  DossierHeader,
  DossierItemCard,
  LinksPreviewCard,
  ScrapeArbitrageModal,
  ScheduleGenerationModal,
  SchedulePublicationModal,
} from './components';

const EditDossier = ({ dossierId, eligibleSites, onBack }) => {
  // États locaux pour les modals
  const [scrapeModal, setScrapeModal] = useState(null);
  const [schedGenModal, setSchedGenModal] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);

  // Hook centralisé pour la logique métier
  const actions = useDossierActions({ dossierId });

  const {
    loading,
    dossier,
    generating,
    starting,
    savingItem,
    itemDrafts,
    publishingItem,
    savingJ0,
    scheduling,
    hasAdvancedItems,
    allItemsGenerated,
    canStartGeneration,
    generatePlan,
    resetPlan,
    defaultScheduleDate,
    scrapeSourcesAndHandle,
    continueWithExistingSources,
    addUrlsAndRetry,
    cancelScheduledGeneration,
    setDraft,
    saveItem,
    saveJ0,
    publishItem,
    confirmScheduleGeneration,
    setStarting,
  } = actions;

  // Mapping sites par ID
  const sitesById = useMemo(() => {
    const m = {};
    eligibleSites.forEach(s => { m[s.id] = s; });
    return m;
  }, [eligibleSites]);

  // Handlers pour les modals et actions
  const handleStartGeneration = () => {
    const scheduledIso = defaultScheduleDate();
    const scheduledLabel = new Date(scheduledIso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    
    const j0 = dossier.j0_date ? new Date(dossier.j0_date) : new Date();
    const pilierDate = new Date(j0);
    pilierDate.setDate(pilierDate.getDate() + 3);
    const pilierLabel = pilierDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    
    const msg = allItemsGenerated
      ? `Tous les articles sont déjà générés. Relancer (programmé pour ${scheduledLabel}) ?`
      : `🚀 LANCEMENT DE LA GÉNÉRATION PROGRAMMÉE\n\n` +
        `📅 Génération des ${dossier.items.length} article(s) :\n` +
        `   → Programmée pour le ${scheduledLabel} (heures creuses)\n\n` +
        `📰 Publication automatique sur WordPress :\n` +
        `   → Pilier : ${pilierLabel} à 10h34\n` +
        `   → Satellites : selon leur décalage (J+offset) à 10h34\n\n` +
        `Les articles seront générés puis DIRECTEMENT PROGRAMMÉS sur WordPress.\n` +
        `Vous n'aurez pas besoin de les publier manuellement.\n\n` +
        `Le scraping des sources va être effectué maintenant pour valider la faisabilité.\n\n` +
        `Continuer ?`;
    if (!window.confirm(msg)) return;
    
    scrapeSourcesAndHandle('schedule', scheduledIso, setScrapeModal);
  };

  const handleStartGenerationImmediate = () => {
    const msg = allItemsGenerated
      ? '⚠️ GÉNÉRATION IMMÉDIATE\n\nTous les articles sont déjà générés.\n\nRelancer MAINTENANT (hors heures creuses) ?\nLes items en échec seront retentés.'
      : `⚠️ GÉNÉRATION IMMÉDIATE (HORS HEURES CREUSES)\n\n` +
        `Vous demandez à générer ${dossier.items.length} article(s) MAINTENANT.\n\n` +
        `⚠️ ATTENTION : En mode immédiat, les articles seront générés mais NE SERONT PAS automatiquement publiés sur WordPress.\n` +
        `Vous devrez les publier manuellement après génération.\n\n` +
        `💡 Pour une publication automatique programmée, utilisez plutôt "Lancer génération (nuit · 01h)".\n\n` +
        `Continuer quand même ?`;
    if (!window.confirm(msg)) return;
    
    scrapeSourcesAndHandle('immediate', null, setScrapeModal);
  };

  const handleContinueWithExisting = async () => {
    await continueWithExistingSources(scrapeModal);
    setScrapeModal(null);
  };

  const handleAddUrlsAndRetry = async (newUrls, onClear) => {
    const result = await addUrlsAndRetry(newUrls, scrapeModal, setScrapeModal);
    if (result.success) {
      setScrapeModal(null);
      onClear();
    }
  };

  const handleConfirmScheduleGeneration = async () => {
    const result = await confirmScheduleGeneration(schedGenModal?.date, () => setSchedGenModal(null));
    if (!result.success && result.scrapeData) {
      setScrapeModal({
        sources: result.scrapeData.sources || [],
        succeeded: result.scrapeData.succeeded || 0,
        failed: result.scrapeData.failed || 0,
        total: result.scrapeData.total || 0,
      });
    }
  };

  const handlePublishNow = (item, site) => {
    if (window.confirm(`Publier immédiatement sur ${site?.name || 'WordPress'} ?\nSlug forcé : ${item.slug || '?'}`)) {
      publishItem(item, { publish_status: 'publish' });
    }
  };

  const openScheduleModal = (item) => {
    const iso = suggestedDateTime(dossier.j0_date, item.planned_publish_offset_days);
    setScheduleModal({ item, date: iso });
  };

  const confirmSchedule = () => {
    if (!scheduleModal) return;
    const localDate = new Date(scheduleModal.date);
    if (isNaN(localDate.getTime())) {
      toast.error('Date invalide');
      return;
    }
    if (localDate.getTime() < Date.now() + 60000) {
      toast.error('La date doit être dans le futur (au moins 1 minute)');
      return;
    }
    publishItem(scheduleModal.item, {
      publish_status: 'future',
      scheduled_date: localDate.toISOString(),
    }, () => setScheduleModal(null));
  };

  // Loading state
  if (loading || !dossier) {
    return <div className="min-h-screen p-8 text-slate-400">Chargement…</div>;
  }

  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="edit-dossier-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <DossierHeader
          dossier={dossier}
          generating={generating}
          starting={starting}
          hasAdvancedItems={hasAdvancedItems}
          allItemsGenerated={allItemsGenerated}
          canStartGeneration={canStartGeneration}
          onBack={onBack}
          onGeneratePlan={generatePlan}
          onResetPlan={resetPlan}
          onStartGeneration={handleStartGeneration}
          onStartGenerationImmediate={handleStartGenerationImmediate}
          onOpenScheduleModal={() => setSchedGenModal({ date: defaultScheduleDate() })}
          onCancelScheduledGeneration={cancelScheduledGeneration}
        />

        {/* Stratégie */}
        {dossier.strategy_summary && (
          <Card className="border-cyan-500/30 bg-slate-900/40 mb-4">
            <CardContent className="p-4">
              <div className="text-xs text-cyan-400 uppercase mb-2 tracking-wider">Stratégie</div>
              <p className="text-sm text-slate-300">{dossier.strategy_summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Date J0 */}
        {allItemsGenerated && (
          <Card className="border-blue-500/30 bg-blue-950/10 mb-4" data-testid="j0-card">
            <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs">
                <CalendarClock className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300">Date de référence (J0) — utilisée pour pré-remplir les dates de planification</span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dossier.j0_date ? dossier.j0_date.slice(0, 10) : ''}
                  onChange={e => saveJ0(e.target.value ? new Date(e.target.value + 'T09:00:00').toISOString() : null)}
                  disabled={savingJ0}
                  className="w-40 h-8 text-xs"
                  data-testid="j0-date-input"
                />
                {dossier.j0_date && (
                  <Button onClick={() => saveJ0(null)} variant="outline" size="sm" className="h-8" data-testid="j0-clear-btn">
                    <XCircle className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Liste des items */}
        {(!dossier.items || dossier.items.length === 0) ? (
          <Card className="border-cyan-500/30 bg-slate-900/40">
            <CardContent className="py-12 text-center">
              <Sparkles className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">Aucun plan généré pour l&apos;instant.</p>
              <p className="text-xs text-slate-500 mt-2">Cliquez sur « Générer le plan » pour produire la grappe SEO via Claude Sonnet 4.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="dossier-items-list">
            {dossier.items.map((item, idx) => (
              <DossierItemCard
                key={item.id}
                item={item}
                idx={idx}
                site={sitesById[item.site_id]}
                dossier={dossier}
                itemDrafts={itemDrafts}
                savingItem={savingItem}
                publishingItem={publishingItem}
                onSetDraft={setDraft}
                onSaveItem={saveItem}
                onPublishNow={handlePublishNow}
                onOpenScheduleModal={openScheduleModal}
              />
            ))}
          </div>
        )}

        {/* Liens de maillage */}
        <LinksPreviewCard dossier={dossier} sitesById={sitesById} />
      </div>

      {/* Modals */}
      <ScrapeArbitrageModal
        scrapeModal={scrapeModal}
        starting={starting}
        onCancel={() => { setScrapeModal(null); setStarting(false); }}
        onContinueWithExisting={handleContinueWithExisting}
        onAddUrlsAndRetry={handleAddUrlsAndRetry}
      />

      <ScheduleGenerationModal
        schedGenModal={schedGenModal}
        scheduling={scheduling}
        onClose={() => setSchedGenModal(null)}
        onDateChange={(date) => setSchedGenModal(m => ({ ...m, date }))}
        onConfirm={handleConfirmScheduleGeneration}
      />

      <SchedulePublicationModal
        scheduleModal={scheduleModal}
        publishingItem={publishingItem}
        onClose={() => setScheduleModal(null)}
        onDateChange={(date) => setScheduleModal(m => ({ ...m, date }))}
        onConfirm={confirmSchedule}
      />
    </div>
  );
};

export default EditDossier;
